/**
 * Shared type contracts between web-execute client and Cloud Functions.
 */

// Canonical status contracts live in the shared state machine so native, web,
// and Cloud Functions stay in lockstep. See packages/task-pipeline.
import type {
  TaskStatus,
  TaskDisplayStatus,
  StatusHistoryEntry,
} from '@x9/task-pipeline';

export type { TaskStatus, TaskDisplayStatus, StatusHistoryEntry };

export interface ResolveTaskResponse {
  ok: true;
  purpose?: 'execute';
  task: {
    id: string;
    title: string;
    description: string | null;
    status: TaskStatus;
    displayStatus?: TaskDisplayStatus;
    creatorName: string;
    dueDate: string | null;
    photoUrl: string | null;
    location?: { latitude: number; longitude: number } | null;
    /** Web-capture scribble (self-contained SVG string). */
    highlightSvg?: string | null;
    /** Mobile-capture scribble — nested paths or Firestore-flat form / JSON string. */
    drawingPaths?: unknown;
    executorTimeline: StatusHistoryEntry[];
    bucketName: string;
    createdAt: string;
    hasOverlay: boolean;
    executionAvailability: 'available' | 'claimed_by_me' | 'claimed_by_other' | 'terminal';
    executorDisplay: string | null;
    assigneeVerificationRequired?: boolean;
    /** False when the signed-in owner is reviewing work assigned to someone else. */
    canExecute?: boolean;
    viewerRole?: 'owner' | 'executor';
  };
  visibility: 'public' | 'private';
}

export interface ResolveAwarenessResponse {
  ok: true;
  purpose: 'awareness';
  preview: {
    title: string;
    status: TaskStatus;
    taskPhotoThumbUrl?: string | null;
  };
  deepLink: string;
  visibility: 'public' | 'private';
}

export interface ResolveTaskDenied {
  ok: false;
  reason:
    | 'not_found'
    | 'expired'
    | 'phone_mismatch'
    | 'auth_required'
    | 'not_assignee'
    | 'link_not_execute';
  hint?: 'private' | 'assignee' | 'follow' | 'executor';
}

export type ResolveTaskResult = ResolveTaskResponse | ResolveAwarenessResponse | ResolveTaskDenied;

export function isAwarenessResolve(
  result: ResolveTaskResult,
): result is ResolveAwarenessResponse {
  return result.ok === true && result.purpose === 'awareness';
}

export function isExecuteResolve(
  result: ResolveTaskResult,
): result is ResolveTaskResponse {
  return result.ok === true && result.purpose !== 'awareness';
}

export interface UpdateStatusRequest {
  shortId: string;
  newStatus: TaskStatus;
  comment?: string;
  /** @deprecated Prefer proofStoragePaths (up to 3). */
  proofStoragePath?: string;
  /** Durable Storage paths for completion proof photos (max 3). */
  proofStoragePaths?: string[];
}

export interface UpdateStatusResponse {
  ok: boolean;
  error?: string;
  assigneeVerificationRequired?: boolean;
  code?:
    | 'already_claimed'
    | 'already_assigned'
    | 'not_available'
    | 'not_claimed'
    | 'not_assignee'
    | 'phone_required'
    | 'assignee_verify_required'
    | 'invalid_transition';
}

export interface EnsureContactForPhoneAuthResponse {
  ok: true;
  contactId: string;
}

export interface FinalizeWebAssigneeResponse {
  ok: boolean;
  contactId?: string;
  alreadyAssigned?: boolean;
  error?: string;
  code?: string;
}

export interface UpdateDueDateRequest {
  shortId: string;
  dueDate: string;
}

export interface RequestProofUploadRequest {
  shortId: string;
  contentType: string;
  size: number;
}

export type RequestProofUploadResponse =
  | { ok: true; uploadUrl: string; storagePath: string }
  | { ok: false; error: string };

export interface UploadProofPhotoRequest {
  shortId: string;
  contentType: string;
  /** Raw base64 (no data: prefix). */
  base64: string;
}

export type UploadProofPhotoResponse =
  | { ok: true; storagePath: string }
  | { ok: false; error: string };

export interface GenerateTaskLinkRequest {
  taskId: string;
  visibility: 'public' | 'private';
}

export interface GenerateTaskLinkResponse {
  ok: true;
  shortId: string;
  url: string;
}
